// ─── src/components/dashboard/RegistrationsPerDayChart.tsx ────────────────────
//
// A small bar chart that answers a single question: "how many people
// registered on each day since the form opened?" — the two useful things
// the ops team can't see from the counts alone:
//
//   1. Where the demand spikes were (campaign nights, WhatsApp blasts).
//   2. Whether registrations are still coming in or the funnel has stalled.
//
// Reads only `createdAt` off each row, so it drops into any of the GEW /
// Awards / Investor Lounge / NPD views that already fetch a full dataset.
// Empty in-between days render as zero bars so the pace stays visually
// honest — a Saturday of no signups looks like nothing happened, not like
// it never existed.

import type { FC } from "react";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface Row {
  /** ISO timestamp, e.g. from a TypeORM `@CreateDateColumn`. */
  createdAt: string;
}

interface Props<T extends Row> {
  rows: T[] | undefined;
  /** Overrides the accent for the bars — defaults to the emerald brand green. */
  color?: string;
  /** Card heading; defaults to "Registrations per day". */
  title?: string;
  /**
   * Cap the visible range to the last N days (defaults to no cap — shows
   * the full range from the first row's date to today).
   */
  lastNDays?: number;
}

// ── Date helpers ─────────────────────────────────────────────────────────────
// Group by the *local* date (YYYY-MM-DD) rather than the UTC one, so a row
// created at 23:00 local doesn't slide onto the next day the ops team sees.
const dayKey = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDayKey = (k: string): Date => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** Short "MMM D" label used along the X-axis. */
const shortLabel = (k: string): string =>
  parseDayKey(k).toLocaleDateString(undefined, { month: "short", day: "numeric" });

/** Full weekday label used inside the hover tooltip. */
const longLabel = (k: string): string =>
  parseDayKey(k).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

/** All day keys between `from` and `to` (inclusive), so gaps render as zeros. */
const daysBetween = (from: Date, to: Date): string[] => {
  const keys: string[] = [];
  const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  while (cursor <= end) {
    keys.push(dayKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
};

// ── Component ────────────────────────────────────────────────────────────────

export function RegistrationsPerDayChart<T extends Row>({
  rows,
  color = "#00DC92",
  title = "Registrations per day",
  lastNDays,
}: Props<T>): ReturnType<FC> {
  const { series, total, peak } = useMemo(() => {
    const data = rows ?? [];
    if (data.length === 0) return { series: [], total: 0, peak: 0 };

    // Bucket rows by their local day key.
    const counts: Record<string, number> = {};
    let earliest: Date | null = null;
    for (const row of data) {
      const d = new Date(row.createdAt);
      if (Number.isNaN(d.getTime())) continue;
      const k = dayKey(d);
      counts[k] = (counts[k] ?? 0) + 1;
      if (!earliest || d < earliest) earliest = d;
    }
    if (!earliest) return { series: [], total: 0, peak: 0 };

    // Fill in zero days between earliest and today (or `lastNDays` back)
    // so periods with no signups aren't visually compressed away.
    const today = new Date();
    const rangeStart =
      lastNDays && lastNDays > 0
        ? new Date(today.getFullYear(), today.getMonth(), today.getDate() - (lastNDays - 1))
        : earliest;
    const startDate = rangeStart > earliest ? rangeStart : earliest;

    const series = daysBetween(startDate, today).map((day) => ({
      day,
      count: counts[day] ?? 0,
      label: shortLabel(day),
      fullLabel: longLabel(day),
    }));

    const peak = series.reduce((max, s) => (s.count > max ? s.count : max), 0);
    const total = series.reduce((sum, s) => sum + s.count, 0);

    return { series, total, peak };
  }, [rows, lastNDays]);

  // Empty state — better than a blank card, and honest about "no data yet".
  if (series.length === 0) {
    return (
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        <p className="mt-4 text-sm text-brand-muted">
          No registrations yet — the chart will appear once the first person signs up.
        </p>
      </div>
    );
  }

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        <p className="text-xs text-brand-muted">
          <span className="text-white font-semibold">{total}</span> across{" "}
          {series.length} day{series.length === 1 ? "" : "s"} · peak{" "}
          <span className="text-white font-semibold">{peak}</span>/day
        </p>
      </div>

      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              // Show at most ~10 tick labels regardless of range, so a
              // multi-week view stays legible without overlap.
              interval={Math.max(0, Math.floor(series.length / 10) - 1)}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              width={40}
            />
            <Tooltip
              cursor={{ fill: "rgba(255,255,255,0.04)" }}
              contentStyle={{
                background: "#0f172a",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                fontSize: 12,
              }}
              labelFormatter={(_label, payload) => {
                const point = payload?.[0]?.payload as { fullLabel?: string } | undefined;
                return point?.fullLabel ?? "";
              }}
              formatter={(value) => {
                const count = typeof value === "number" ? value : 0;
                return [`${count} registration${count === 1 ? "" : "s"}`, ""] as [string, string];
              }}
              separator=""
            />
            <Bar dataKey="count" fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
